import { useState } from 'react';
import type { BottleContent } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, FieldRow, SegRow } from './SheetShell.tsx';
import { fmtTime } from '../../lib/format.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultAmount?: number;
  onSubmit: (vars: { amount_ml: number; content: BottleContent }) => Promise<unknown> | void;
};

const PRESETS = [60, 90, 120, 140, 180];

export function BottleSheet({ open, onClose, defaultAmount = 140, onSubmit }: Props) {
  const [amount, setAmount] = useState(defaultAmount);
  const [content, setContent] = useState<BottleContent>('expressed');

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title="Log bottle"
      tagline={`Last bottle ${defaultAmount} ml`}
      accent="bottle"
      icon={(p) => <Icon.bottle {...p} />}
      primaryLabel="Log bottle"
      onPrimary={async () => {
        await onSubmit({ amount_ml: amount, content });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          style={{
            padding: '16px 18px',
            borderRadius: 16,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.04,
            }}
          >
            Amount
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 6,
            }}
          >
            <button
              type="button"
              onClick={() => setAmount((a) => Math.max(0, a - 10))}
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: 'var(--bg-sunken)',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              −
            </button>
            <div
              className="lt-serif lt-tabular"
              style={{ fontSize: 38, fontWeight: 400, letterSpacing: -0.02 }}
            >
              {amount} <span style={{ fontSize: 18, color: 'var(--text-3)' }}>ml</span>
            </div>
            <button
              type="button"
              onClick={() => setAmount((a) => Math.min(2000, a + 10))}
              style={{
                width: 36,
                height: 36,
                borderRadius: 999,
                background: 'var(--bg-sunken)',
                fontSize: 18,
                fontWeight: 600,
              }}
            >
              +
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            {PRESETS.map((v) => {
              const on = v === amount;
              return (
                <button
                  type="button"
                  key={v}
                  onClick={() => setAmount(v)}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '6px 0',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 500,
                    background: on ? 'var(--bottle)' : 'var(--bg-sunken)',
                    color: on ? '#fff' : 'var(--text-2)',
                  }}
                >
                  {v}
                </button>
              );
            })}
          </div>
        </div>
        <FieldRow label="Time" value={fmtTime(Math.floor(Date.now() / 1000))} hint="now" />
        <SegRow
          label="Contents"
          active={content}
          onChange={setContent}
          options={[
            { id: 'formula', label: 'Formula' },
            { id: 'expressed', label: 'Expressed' },
            { id: 'mix', label: 'Mix' },
          ]}
        />
      </div>
    </SheetShell>
  );
}
